import camelCase from 'lodash/camelCase';
import {
  blue,
  grey,
} from '@fortress-technology-solutions/fortress-component-library/design/index';
import complianceMessages from '../ComplianceOverview/messages';
import messages from './messages';
import { developmentPhaseFilterFn, propertyTypeFilterFn } from './utils';

export const STATUS_FILTER_OPTIONS = [
  { text: 'Active', value: 'Active' },
  { text: 'Inactive', value: 'Inactive' },
];

export const getHeaders = ({
  propertyRoles,
  propertyRoleAssignments,
  pastDueColumn,
  addlPropDetailsMiMoColumns = false,
  devPhaseDropDown,
  mixedValueComposition,
  intl,
}) => {
  const propertyRoleHeaders = propertyRoleAssignments
    ? propertyRoles.map((role) => ({
        id: camelCase(role),
        label: role,
        sortable: true,
        searchable: true,
        dataType: 'string',
        sx: { backgroundColor: blue.light },
        multiselect: true,
      }))
    : [];

  const pastDueCertificationsHeader = {
    id: 'pastDueCertifications',
    label: 'Past Due Certifications',
    sortable: true,
    sx: { backgroundColor: blue.main },
    dataType: 'number',
    info: intl?.formatMessage(complianceMessages.pastDueTooltip),
  };
  const developmentPhaseHeader = {
    id: 'developmentPhase',
    label: 'Development Phase',
    sortable: true,
    multiselect: true,
    filterOptions: null,
    dataType: 'string',
    sx: { backgroundColor: grey.lighter, color: 'text.primary' },
  };
  const mixedValueCompositionHeader = {
    id: 'propertyType',
    label: 'Property Type',
    sortable: true,
    multiselect: true,
    filterOptions: null,
    dataType: 'string',
    sx: { backgroundColor: grey.lighter, color: 'text.primary' },
  };
  const HEADERS = [
    {
      id: 'propertyName',
      label: 'Property',
      sortable: true,
      searchable: true,
      dataType: 'string',
      sx: { backgroundColor: grey.lighter, color: 'text.primary' },
    },
    ...(mixedValueComposition ? [mixedValueCompositionHeader] : []),
    ...(devPhaseDropDown ? [developmentPhaseHeader] : []),
    {
      id: 'propertyStatus',
      label: 'Property Status',
      sortable: true,
      multiselect: true,
      filterOptions: STATUS_FILTER_OPTIONS,
      sx: { backgroundColor: grey.lighter, color: 'text.primary' },
      dataType: 'string',
    },
    {
      id: 'numTotalUnits',
      label: 'Total Units',
      sortable: true,
      sx: { backgroundColor: grey.lighter, color: 'text.primary' },
      dataType: 'number',
    },
    {
      id: 'numOccupiedUnits',
      label: 'Occupied Units',
      sortable: true,
      sx: { backgroundColor: blue.light },
      dataType: 'number',
    },
    {
      id: 'vacantUnits',
      label: 'Vacant Units',
      sortable: true,
      sx: { backgroundColor: blue.light },
      dataType: 'number',
    },
    {
      id: 'currentOccupancy',
      label: 'Current Occupancy',
      sortable: true,
      sx: { backgroundColor: blue.light },
      dataType: 'number',
    },
    {
      id: 'netOccupancy',
      label: '30-Day Net %',
      sortable: true,
      sx: { backgroundColor: blue.light },
      dataType: 'number',
    },
    {
      id: 'delinquencyPercent',
      label: 'Collection Deposits % of Charges',
      sortable: true,
      sx: { backgroundColor: blue.main },
      dataType: 'number',
    },
    {
      id: 'delinquencyChargesTotal',
      label: 'PTD Charges',
      sortable: true,
      sx: { backgroundColor: blue.light },
      dataType: 'number',
    },
    {
      id: 'mtdDeposits',
      label: 'PTD Deposits',
      sortable: true,
      sx: { backgroundColor: blue.light },
      dataType: 'number',
    },
    {
      id: 'pendingDeposits',
      label: 'PTD Pending Deposits',
      sortable: true,
      sx: { backgroundColor: blue.light },
      dataType: 'number',
    },
    {
      id: 'concessions',
      label: 'PTD Concessions',
      sortable: true,
      sx: { backgroundColor: blue.light },
      dataType: 'number',
    },
    ...(addlPropDetailsMiMoColumns
      ? [
          {
            id: 'approvedScheduledMoveIns',
            label: 'Approved Scheduled Move-ins',
            sortable: true,
            sx: { backgroundColor: blue.main },
            dataType: 'number',
            info: intl?.formatMessage(messages.approvedScheduledMoveInsTooltip),
          },
          {
            id: 'mtdMoveIns',
            label: 'MTD Move-ins',
            sortable: true,
            sx: { backgroundColor: blue.main },
            dataType: 'number',
            info: intl?.formatMessage(messages.monthToDateMoveInsTooltip),
          },
          {
            id: 'scheduledMoveOuts',
            label: 'Scheduled Move-outs',
            sortable: true,
            sx: { backgroundColor: blue.main },
            dataType: 'number',
            info: intl?.formatMessage(messages.scheduledMoveOutsTooltip),
          },
          {
            id: 'mtdMoveOuts',
            label: 'MTD Move-outs',
            sortable: true,
            sx: { backgroundColor: blue.main },
            dataType: 'number',
            info: intl?.formatMessage(messages.monthToDateMoveOutsTooltip),
          },
        ]
      : []),
    {
      id: 'numWorkOrdersOpen',
      label: 'Open Work Orders',
      sortable: true,
      sx: { backgroundColor: blue.dark },
      dataType: 'number',
    },
    {
      id: 'avgWorkOrderDaysOpen',
      label: 'Avg Days Open',
      sortable: true,
      sx: { backgroundColor: blue.main },
      dataType: 'number',
    },
    {
      id: 'numNtvCurrentMonth',
      label: 'NTV - Current Month',
      sortable: true,
      sx: { backgroundColor: blue.light },
      dataType: 'number',
    },
    {
      id: 'numNtvNextMonth',
      label: 'NTV - Next Mo',
      sortable: true,
      sx: { backgroundColor: blue.light },
      dataType: 'number',
    },
    {
      id: 'numGrossLeasesApproved',
      label: 'Gross Leases - Approved',
      sortable: true,
      sx: { backgroundColor: blue.main },
      dataType: 'number',
    },
    {
      id: 'numGrossLeasesNotApproved',
      label: 'Gross Leases - Not Approved',
      sortable: true,
      sx: { backgroundColor: blue.main },
      dataType: 'number',
    },
    {
      id: 'numAvailable',
      label: 'Available Units',
      sortable: true,
      sx: { backgroundColor: blue.main },
      dataType: 'number',
    },
    ...(pastDueColumn ? [pastDueCertificationsHeader] : []),
    ...propertyRoleHeaders,
  ];
  return HEADERS;
};

export const getSectionHeaders = ({
  addlPropDetailsMiMoColumns = false,
  devPhaseDropDown,
  mixedValueComposition,
}) => [
  {
    label: '',
    colSpan: (mixedValueComposition ? 1 : 0) + (devPhaseDropDown ? 1 : 0) + 2,
    sx: { backgroundColor: grey.lighter },
  },
  {
    label: 'Occupancy',
    colSpan: 5,
    sx: { backgroundColor: blue.main },
  },
  {
    label: 'Collections',
    colSpan: 1,
    sx: { backgroundColor: blue.dark },
  },
  { label: 'PTD', colSpan: 4, sx: { backgroundColor: blue.main } },
  {
    label: 'Move-in/Move-out Activity',
    colSpan: addlPropDetailsMiMoColumns ? 4 : 0,
    sx: { backgroundColor: blue.dark },
  },
  {
    label: 'Work Orders',
    colSpan: 2,
    sx: { backgroundColor: blue.main },
  },
  {
    label: 'Notice to Vacates',
    colSpan: 2,
    sx: { backgroundColor: blue.dark },
  },
  {
    label: 'Leasing',
    colSpan: 3,
    sx: { backgroundColor: blue.main },
  },
];

// Keep the old export for backward compatibility (will be removed when we update all usage)
export const SECTION_HEADERS = getSectionHeaders({
  addlPropDetailsMiMoColumns: false,
});

export const getPropertyPathMap = ({
  propertyRoles,
  pastDueColumn,
  addlPropDetailsMiMoColumns = false,
  devPhaseDropDown,
  mixedValueComposition,
}) => {
  const propertyRolePathMap = propertyRoles
    .map((role) => ({
      [camelCase(role)]: {
        path: camelCase(role),
        dataType: 'string',
      },
    }))
    .reduce((acc, prop) => {
      return {
        ...acc,
        ...prop,
      };
    }, {});

  const pastDueCertificationsPropertyPathMap = {
    pastDueCertifications: {
      path: 'pastDueCertifications',
      dataType: 'number',
    },
  };
  const developmentPhasePathMap = {
    developmentPhase: {
      path: 'developmentPhase',
      dataType: 'string',
      filterFn: developmentPhaseFilterFn,
    },
  };
  const mixedValueCompositionPathMap = {
    propertyType: {
      path: 'propertyType',
      dataType: 'string',
      filterFn: propertyTypeFilterFn,
    },
  };
  const PROPERTY_PATH_MAP = {
    propertyName: {
      path: 'propertyName',
      dataType: 'string',
    },
    ...(mixedValueComposition ? mixedValueCompositionPathMap : {}),
    ...(devPhaseDropDown ? developmentPhasePathMap : {}),
    propertyStatus: {
      path: 'propertyStatus',
      dataType: 'string',
    },
    numTotalUnits: {
      path: 'numTotalUnits',
      dataType: 'number',
    },
    numOccupiedUnits: {
      path: 'numOccupiedUnits',
      dataType: 'number',
    },
    vacantUnits: {
      path: 'vacantUnits',
      dataType: 'number',
    },
    currentOccupancy: {
      path: 'currentOccupancy',
      dataType: 'number',
    },
    netOccupancy: {
      path: 'netOccupancy',
      dataType: 'number',
    },
    delinquencyPercent: {
      path: 'delinquencyPercent',
      dataType: 'number',
    },
    delinquencyChargesTotal: {
      path: 'delinquencyChargesTotal',
      dataType: 'number',
    },
    mtdDeposits: {
      path: 'mtdDeposits',
      dataType: 'number',
    },
    pendingDeposits: {
      path: 'pendingDeposits',
      dataType: 'number',
    },
    concessions: {
      path: 'concessions',
      dataType: 'number',
    },
    numWorkOrdersOpen: {
      path: 'numWorkOrdersOpen',
      dataType: 'number',
    },
    numNtvCurrentMonth: {
      path: 'numNtvCurrentMonth',
      dataType: 'number',
    },
    numNtvNextMonth: {
      path: 'numNtvNextMonth',
      dataType: 'number',
    },
    numGrossLeasesApproved: {
      path: 'numGrossLeasesApproved',
      dataType: 'number',
    },
    numGrossLeasesNotApproved: {
      path: 'numGrossLeasesNotApproved',
      dataType: 'number',
    },
    numAvailable: {
      path: 'numAvailable',
      dataType: 'number',
    },
    ...(addlPropDetailsMiMoColumns
      ? {
          approvedScheduledMoveIns: {
            path: 'approvedScheduledMoveIns',
            dataType: 'number',
          },
          mtdMoveIns: {
            path: 'mtdMoveIns',
            dataType: 'number',
          },
          scheduledMoveOuts: {
            path: 'scheduledMoveOuts',
            dataType: 'number',
          },
          mtdMoveOuts: {
            path: 'mtdMoveOuts',
            dataType: 'number',
          },
        }
      : {}),
    ...(pastDueColumn ? pastDueCertificationsPropertyPathMap : {}),
    ...propertyRolePathMap,
  };
  return PROPERTY_PATH_MAP;
};
